import { action, computed, observable, runInAction } from 'mobx';
import {
	bookingsApi,
	UpcomingBookingsDataStructure,
	CancellationFee,
} from 'AppClient/api/Bookings';
import { find, isEmpty, without } from 'lodash-es';

import { RootStore } from 'AppClient/stores';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { CreditCard, BookingCreditCard } from 'app-types';

export class UpcomingBookingsStore {
	@observable private _upcomingBookings: UpcomingBookingsDataStructure[] = [];
	@observable private _fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable private _cancellationFee: CancellationFee = {} as CancellationFee;

	constructor(private rootStore: RootStore) {}

	@computed
	get cancellationFee(): CancellationFee {
		return this._cancellationFee;
	}

	@computed
	get isIdle(): boolean {
		return this._fetchStatus === FetchStatus.IDLE;
	}

	@computed
	get isPending(): boolean {
		return this._fetchStatus === FetchStatus.PENDING;
	}

	@computed
	get isSuccess(): boolean {
		return this._fetchStatus === FetchStatus.SUCCESS;
	}

	@computed
	get isLoadingUpcoming() {
		return this.isIdle || this.isPending;
	}

	@computed
	get getTotalCount() {
		return this._upcomingBookings.length;
	}

	@computed
	get upcomingBookingsData() {
		return this._upcomingBookings;
	}

	@computed
	get hasUpcomingData() {
		return !isEmpty(this._upcomingBookings);
	}
	@action.bound
	async receiveUpcomingBookings() {
		try {
			const response = await bookingsApi.getUpcomingBookings();
			const { result, error } = response.data;
			if (error) {
				this._fetchStatus = FetchStatus.FAILURE;
				this.rootStore.alertStore.setErrorAlert(error);
				return;
			}
			if (result) {
				const { upcomingBookings } = result;
				if (upcomingBookings) {
					runInAction(() => {
						this._fetchStatus = FetchStatus.SUCCESS;
						this._upcomingBookings = upcomingBookings;
					});
				}
			}
		} catch (error) {
			this.rootStore.alertStore.setRequestError(error);
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}

	@action.bound
	removeBooking(bookingId: number) {
		const rm = find(this._upcomingBookings, { bookingId });
		if (rm) {
			this._upcomingBookings = without(this._upcomingBookings, rm);
		}
	}

	@action.bound
	async cancelUpcomingTrip(bookingId: number) {
		this._fetchStatus = FetchStatus.PENDING;
		try {
			const response = await bookingsApi.cancelBooking({ bookingId });
			const { error } = response.data;
			if (error) {
				this._fetchStatus = FetchStatus.FAILURE;
				this.rootStore.alertStore.setErrorAlert(error);
				return;
			}
			runInAction(() => {
				this.rootStore.pastBookingsStore.setIsDataFromOutside(true);
				this.rootStore.pastBookingsStore.receivePastBookings();
				this._fetchStatus = FetchStatus.SUCCESS;
				this.removeBooking(bookingId);
			});
		} catch (error) {
			this.rootStore.alertStore.setRequestError(error);
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}

	@action.bound
	findUpcomingById(bookingId: number): UpcomingBookingsDataStructure | undefined {
		return find(this.upcomingBookingsData, { bookingId });
	}

	@action.bound
	async getCancellationFee(
		bookingId: number,
		callback: (creditCard?: CreditCard | BookingCreditCard) => void
	) {
		this._fetchStatus = FetchStatus.PENDING;
		try {
			const response = await bookingsApi.getCencellationFee({ bookingId });
			const { result, error } = response.data;
			if (error) {
				this._fetchStatus = FetchStatus.FAILURE;
				this.rootStore.alertStore.setErrorAlert(error);
				return;
			}
			if (result) {
				runInAction(() => {
					this._cancellationFee = result;
				});
				const creditCard = await this.rootStore.bookingCCStore.getCreditCardByBookingId(
					bookingId
				);

				callback(creditCard);
				this._fetchStatus = FetchStatus.SUCCESS;
			}
		} catch (error) {
			this.rootStore.alertStore.setRequestError(error);
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}
}
